import { useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import { exchangeSsoCodeApi } from '@api/auth';
import { resetStep, setSessionToken } from '@store/slices/login.slice';
import type { RootState } from '@utils/types';
import {
  clearPartnerSession,
  setPartnerSession,
} from '@store/slices/partnerSession.slice';
import { getAccessToken, isTokenExpired } from '@utils/helpers';
import {
  getSsoCityFromUrl,
  getSsoCodeFromUrl,
  getSsoTokenFromUrl,
  parsePartnerSsoToken,
  stripTokenFromUrl,
} from '@utils/partnerSession';
import {
  addSsoSessionStartedBreadcrumb,
  captureSsoCodeExchangeFailure,
  captureSsoSessionStarted,
  captureSsoTokenRejection,
} from '@utils/ssoObservability';
import { getPartnerSessionErrorPath } from '@utils/partnerSessionError';
import { emitPartnerObservabilityEvent } from '@utils/partnerObservability';

const ssoCodeExchangePromises = new Map<string, Promise<string>>();
const LEGACY_TOKEN_ENVS = new Set(['development', 'devstack', 'test']);

export const clearSsoCodeExchangeCacheForTests = (): void => {
  if (process.env.NODE_ENV === 'test') {
    ssoCodeExchangePromises.clear();
  }
};

const isLegacySsoTokenLaunchEnabled = (): boolean => {
  const appEnv = process.env.VITE_APP_ENV || process.env.NODE_ENV || '';
  return LEGACY_TOKEN_ENVS.has(appEnv);
};

export const exchangeSsoCodeOnce = (code: string): Promise<string> => {
  const existingExchange = ssoCodeExchangePromises.get(code);

  if (existingExchange) {
    return existingExchange;
  }

  const exchangePromise = exchangeSsoCodeApi(code).finally(() => {
    ssoCodeExchangePromises.delete(code);
  });
  ssoCodeExchangePromises.set(code, exchangePromise);

  return exchangePromise;
};

const TokenGuard = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch();
  const persistedPartnerSession = useSelector(
    (state: RootState) => state.partnerSession.partnerSession,
  );
  const persistedSelectedCity = useSelector(
    (state: RootState) => state.login.selectedCity,
  );
  const persistedLoginToken = useSelector(
    (state: RootState) => state.login.token,
  );
  const [isReady, setIsReady] = useState(false);

  useLayoutEffect(() => {
    let isMounted = true;
    const launchHref = window.location.href;
    const incomingCode = getSsoCodeFromUrl(launchHref);
    const rawIncomingToken = getSsoTokenFromUrl(launchHref);
    const incomingToken = isLegacySsoTokenLaunchEnabled()
      ? rawIncomingToken
      : null;
    const ssoCity = getSsoCityFromUrl(launchHref);

    const markReady = () => {
      if (isMounted) {
        setIsReady(true);
      }
    };

    const applySsoToken = (token: string) => {
      const tokenResult = parsePartnerSsoToken(token, {
        city: ssoCity,
      });

      if (tokenResult.status === 'partner') {
        addSsoSessionStartedBreadcrumb(tokenResult.partnerSession.partnerId);
        captureSsoSessionStarted(tokenResult.partnerSession.partnerId);
        emitPartnerObservabilityEvent({
          event: 'session_started',
          partnerSession: tokenResult.partnerSession,
        });
        dispatch(
          setSessionToken({
            token: tokenResult.token,
            city: tokenResult.partnerSession.city,
          }),
        );
        dispatch(setPartnerSession(tokenResult.partnerSession));
      } else {
        captureSsoTokenRejection(tokenResult.status, {
          hasPartnerMerchantId:
            'claims' in tokenResult
              ? Boolean(tokenResult.claims.partner_merchant_id)
              : undefined,
          source:
            'claims' in tokenResult
              ? tokenResult.claims.source?.trim()
              : undefined,
        });
        emitPartnerObservabilityEvent({
          event:
            tokenResult.status === 'expired'
              ? 'session_expired'
              : 'session_failed',
          reason: tokenResult.status,
        });
        dispatch(clearPartnerSession());
        dispatch(resetStep());
        window.history.replaceState(
          window.history.state,
          document.title,
          getPartnerSessionErrorPath(
            tokenResult.status === 'expired'
              ? 'expired_token'
              : tokenResult.status === 'invalid'
                ? 'invalid_token'
                : 'rejected_token',
          ),
        );
      }
    };

    const routeToPartnerError = (
      reason: 'invalid_token' | 'expired_token' | 'rejected_token',
      event: 'session_failed' | 'session_expired',
      eventReason: string = reason,
    ) => {
      emitPartnerObservabilityEvent({
        event,
        reason: eventReason,
        partnerSession: persistedPartnerSession,
      });
      dispatch(clearPartnerSession());
      dispatch(resetStep());
      window.history.replaceState(
        window.history.state,
        document.title,
        getPartnerSessionErrorPath(reason),
      );
    };

    const validateExistingSession = () => {
      const token = getAccessToken();
      if (!token || isTokenExpired(token)) {
        if (persistedPartnerSession) {
          routeToPartnerError('expired_token', 'session_expired');
          return;
        }

        dispatch(clearPartnerSession());
        dispatch(resetStep());
      } else {
        const tokenResult = parsePartnerSsoToken(token, {
          city: persistedSelectedCity || undefined,
        });

        if (tokenResult.status === 'partner') {
          if (persistedLoginToken !== tokenResult.token) {
            dispatch(
              setSessionToken({
                token: tokenResult.token,
                city: tokenResult.partnerSession.city,
              }),
            );
          }

          if (!persistedPartnerSession) {
            dispatch(setPartnerSession(tokenResult.partnerSession));
          }
          return;
        }

        dispatch(clearPartnerSession());
      }
    };

    const failCodeExchange = () => {
      routeToPartnerError(
        'invalid_token',
        'session_failed',
        'code_exchange_failed',
      );
    };

    if (incomingCode || rawIncomingToken) {
      window.history.replaceState(
        window.history.state,
        document.title,
        stripTokenFromUrl(launchHref),
      );

      if (incomingCode) {
        exchangeSsoCodeOnce(incomingCode)
          .then((token) => {
            if (token) {
              applySsoToken(token);
            } else {
              captureSsoCodeExchangeFailure(
                new Error('BLR Pulse SSO code exchange returned empty token'),
              );
              failCodeExchange();
            }
          })
          .catch((error) => {
            captureSsoCodeExchangeFailure(error);
            failCodeExchange();
          })
          .finally(markReady);

        return () => {
          isMounted = false;
        };
      }

      if (incomingToken) {
        applySsoToken(incomingToken);
      } else {
        validateExistingSession();
      }
      markReady();

      return () => {
        isMounted = false;
      };
    }

    validateExistingSession();

    markReady();

    return () => {
      isMounted = false;
    };
  }, [
    dispatch,
    persistedLoginToken,
    persistedPartnerSession,
    persistedSelectedCity,
  ]);

  if (!isReady) {
    return (
      <Box
        alignItems="center"
        display="flex"
        height="100vh"
        justifyContent="center"
        width="100%"
      >
        <CircularProgress aria-label="Loading BLR Pulse session" />
      </Box>
    );
  }

  return <>{children}</>;
};

export default TokenGuard;
